import type { TransactionFilters, TransactionWithCategory } from "@/types";
import { cfApi, cfFetch } from "../api/apiClient";

interface TransactionsResponse {
  transactions: TransactionWithCategory[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

function buildPageUrl(
  filters: Omit<TransactionFilters, "page" | "pageSize">,
  page: number,
  pageSize: number,
): string {
  const params = new URLSearchParams();
  if (filters.search) params.set("search", filters.search);
  if (filters.categoryId) params.set("categoryId", filters.categoryId);
  if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
  if (filters.dateTo) params.set("dateTo", filters.dateTo);
  if (filters.type) params.set("type", filters.type);
  if (filters.currency) params.set("currency", filters.currency);
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));
  return cfApi(`/transactions?${params.toString()}`);
}

/** Fetches all pages matching filters. */
export async function fetchAllTransactionsForExport(
  filters: TransactionFilters,
): Promise<TransactionWithCategory[]> {
  const pageSize = 200;
  let page = 1;
  let totalPages: number | undefined;
  const out: TransactionWithCategory[] = [];
  const { search, categoryId, dateFrom, dateTo, type, currency } = filters;

  const base = {
    search,
    categoryId,
    dateFrom,
    dateTo,
    type,
    currency,
  };

  do {
    const url = buildPageUrl(base, page, pageSize);
    const res = await cfFetch(url);
    const json = (await res.json()) as TransactionsResponse;
    if (!res.ok) throw new Error("Export failed");
    out.push(...json.transactions);
    totalPages = json.totalPages || 1;
    page += 1;
  } while (page <= (totalPages ?? 1));

  return out;
}

export function downloadTransactionsCsv(
  rows: TransactionWithCategory[],
  filename = "transactions-export.csv",
): void {
  const header = [
    "date",
    "description",
    "original_description",
    "category",
    "amount",
    "currency",
    "balance",
  ];
  const lines = [
    header.join(","),
    ...rows.map((tx) =>
      [
        tx.date,
        csvEscape(tx.description),
        csvEscape(tx.originalDescription),
        csvEscape(tx.category?.name ?? ""),
        tx.amount,
        tx.currency,
        tx.balance ?? "",
      ].join(","),
    ),
  ];
  const blob = new Blob([lines.join("\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function csvEscape(s: string): string {
  const needs = /[",\n]/.test(s);
  const t = s.replace(/"/g, '""');
  return needs ? `"${t}"` : t;
}
